<template>
    <div class="tw:flex tw:flex-col tw:gap-4">
        <!-- Header de sección -->
        <h2 v-if="!loading" class="tw:text-xl tw:font-bold tw:text-slate-900 tw:px-1">
            {{ $t('perfil_invitaciones.eventos_disponibles_titulo') }}
        </h2>

        <!-- Skeleton state -->
        <template v-if="loading">
            <SkeletonItem height="tw:h-6" custom-class="tw:w-[168px]" />
            <Skeleton v-for="n in 3" :key="n" customClass="tw:py-[10px] tw:px-2 tw:sm:px-5">
                <template #skeleton>
                    <div class="tw:flex tw:flex-col tw:sm:flex-row tw:items-center tw:gap-[13px]">
                        <div class="tw:flex tw:gap-4 tw:items-center tw:w-full">
                            <SkeletonItem height="tw:h-12" custom-class="tw:w-12 tw:rounded-md tw:flex-shrink-0" />
                            <div class="tw:flex-1 tw:flex tw:flex-col tw:gap-2">
                                <SkeletonItem height="tw:h-4" custom-class="tw:w-3/4" />
                                <SkeletonItem height="tw:h-3" custom-class="tw:w-1/2" />
                                <SkeletonItem height="tw:h-3" custom-class="tw:w-1/3" />
                            </div>
                        </div>
                        <SkeletonItem height="tw:h-10" custom-class="tw:rounded-md tw:flex-shrink-0" class="tw:w-full tw:sm:w-40 "/>
                    </div>
                </template>
            </Skeleton>
        </template>

        <!-- Error state -->
        <template v-else-if="error">
            <CardFront custom-classes="tw:border tw:border-red-100">
                <p class="tw:text-sm tw:text-red-600">
                    {{ $t('perfil_invitaciones.error_cargar_eventos') }}
                </p>
            </CardFront>
        </template>

        <!-- Empty state -->
        <template v-else-if="!eventos.length">
            <CardFront custom-classes="tw:border tw:border-dashed tw:border-gray-200">
                <div class="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:py-8 tw:text-center">
                    <i class="material-icons tw:text-gray-300 tw:text-4xl">event_busy</i>
                    <p class="tw:text-sm tw:text-gray-400">
                        {{ $t('perfil_invitaciones.sin_eventos_disponibles') }}
                    </p>
                </div>
            </CardFront>
        </template>

        <!-- Lista de eventos -->
        <template v-else>
            <CardFront
                v-for="evento in eventos"
                :key="evento.id"
                custom-classes="tw:border tw:border-slate-100 tw:px-2 tw:py-2 tw:sm:px-5 tw:sm:py-4"
            >
                <!-- 4 zonas: fecha | info central | límite solicitud | CTA -->
                <div class="tw:flex tw:flex-col tw:sm:flex-row tw:items-center tw:gap-4 tw:justify-between">
                    <div class="tw:flex tw:gap-4 tw:items-center tw:w-full">
                        <!-- ZONA 1 — Bloque fecha compacto -->
                        <div class="tw:w-12 tw:h-12 tw:flex-shrink-0 tw:flex tw:flex-col tw:items-center tw:justify-center tw:bg-slate-50 tw:border tw:border-slate-200 tw:rounded-lg tw:py-2 tw:px-1 tw:text-slate-700">
                            <span class="tw:text-[9px] tw:font-bold tw:uppercase tw:leading-none tw:tracking-widest tw:text-slate-500">
                                {{ parsedFecha(evento).mes }}
                            </span>
                            <span class="tw:text-xl tw:font-black tw:leading-none tw:mt-0.5 tw:text-slate-900">
                                {{ parsedFecha(evento).dia }}
                            </span>
                        </div>

                        <!-- ZONA 2 — Info principal (flex-1 para ocupar espacio central) -->
                        <div>
                            <!-- Título: matchup o nombre del evento -->
                            <h3 class="tw:font-semibold tw:text-slate-900 tw:text-sm tw:leading-snug tw:truncate">
                                <template v-if="equipo_local(evento) && equipo_visitante(evento)">
                                    {{ equipo_local(evento) }}
                                    <span class="tw:text-slate-400 tw:font-normal tw:mx-0.5">{{ $t('perfil_invitaciones.vs') }}</span>
                                    {{ equipo_visitante(evento) }}
                                </template>
                                <template v-else>
                                    {{ evento.nombre ?? evento.name }}
                                </template>
                            </h3>
                            <!-- Línea secundaria: recinto · división · hora -->
                            <p class="tw:text-slate-400 tw:text-xs tw:mt-0.5 tw:flex tw:flex-wrap tw:gap-x-1 tw:truncate tw:mb-0">
                                <span v-if="lugar(evento)">{{ lugar(evento) }}</span>
                                <span v-if="lugar(evento) && division(evento)" class="tw:text-slate-300">·</span>
                                <span v-if="division(evento)">{{ division(evento) }}</span>
                                <span v-if="(lugar(evento) || division(evento)) && parsedFecha(evento).hora" class="tw:text-slate-300">·</span>
                                <span v-if="parsedFecha(evento).hora">{{ parsedFecha(evento).hora }}</span>
                            </p>
                        </div>
                    </div>

                    <!-- ZONA 3 — Límite solicitud (oculto si no existe el dato) -->
                    <div
                        v-if="fechaLimiteSolicitud(evento)"
                        class="tw:flex-shrink-0 tw:text-right tw:sm:block"
                    >
                        <p class="tw:text-[10px] tw:font-semibold tw:uppercase tw:tracking-wide tw:text-slate-400 tw:leading-none">
                            {{ $t('perfil_invitaciones.limite_solicitud') }}
                        </p>
                        <p class="tw:text-sm tw:font-semibold tw:text-slate-700 tw:mt-1 tw:leading-none">
                            {{ fechaLimiteSolicitud(evento) }}
                        </p>
                    </div>

                    <!-- ZONA 4 — CTA compacto al extremo derecho -->
                    <div class="tw:w-full tw:sm:w-auto">
                        <!-- CTA activo -->
                        <Button
                            v-if="canGenerar(evento)"
                            variant="alt"
                            class="tw:w-full tw:sm:w-fit tw:justify-center"
                            @click="$emit('generar', evento)"
                        >
                            <span class="tw:flex tw:items-center tw:gap-4 tw:whitespace-nowrap">
                                <i class="material-icons tw:text-base tw:leading-none">mail</i>
                                {{ $t('perfil_invitaciones.generar_invitacion') }}
                            </span>
                        </Button>

                        <!-- Estado: invitaciones globalmente agotadas -->
                        <span
                            v-else-if="evento.all_invitaciones_consumidas"
                            class="tw:inline-flex tw:items-center tw:gap-1 tw:rounded-lg tw:bg-slate-100 tw:px-3 tw:py-2 tw:text-xs tw:font-semibold tw:text-slate-400 tw:whitespace-nowrap"
                        >
                            <i class="material-icons tw:text-sm tw:leading-none">block</i>
                            {{ $t('perfil_invitaciones.invitaciones_agotadas') }}
                        </span>

                        <!-- Estado: sin invitaciones propias restantes -->
                        <span
                            v-else
                            class="tw:inline-flex tw:items-center tw:gap-1 tw:rounded-lg tw:bg-amber-50 tw:px-3 tw:py-2 tw:text-xs tw:font-semibold tw:text-amber-600 tw:whitespace-nowrap"
                        >
                            <i class="material-icons tw:text-sm tw:leading-none">hourglass_empty</i>
                            {{ $t('perfil_invitaciones.sin_invitaciones_propias') }}
                        </span>
                    </div>

                </div>
            </CardFront>
        </template>
    </div>
</template>

<script setup>
    import Button from '@/modules/front/ui/Button.vue';
    import CardFront from '@/modules/front/ui/CardFront.vue';
    import Skeleton from '@/modules/front/ui/skeleton/Skeleton.vue';
    import SkeletonItem from '@/modules/front/ui/skeleton/SkeletonItem.vue';
    import { parseFechaSesion } from '@/utils/dateUtils';

    defineProps({
        /**
         * Array de eventos disponibles para generar invitaciones.
         * Shape real del payload (spread completo del API):
         *   { id, name/nombre, fecha_sesion, fecha_fin_sesion,
         *     equipo_local: { nombre }, equipo_visitante: { nombre },
         *     division: { nombre }, author, place: { name },
         *     invitaciones_restantes, all_invitaciones_consumidas }
         */
        eventos: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        error: {
            type: [String, Boolean],
            default: null,
        },
    });

    defineEmits(['generar']);

    // ─── Helpers de extracción de datos del payload ───────────────────────────

    /**
     * Parsea fecha_sesion usando el helper reutilizable de dateUtils.
     * Devuelve { mes, dia, hora } listos para el bloque de fecha.
     */
    const parsedFecha = evento => parseFechaSesion(evento.fecha_sesion ?? evento.fecha ?? null);

    /** Nombre del equipo local, si existe en el payload */
    const equipo_local = evento => evento.equipo_local?.nombre ?? null;

    /** Nombre del equipo visitante, si existe en el payload */
    const equipo_visitante = evento => evento.equipo_visitante?.nombre ?? null;

    /** Nombre del recinto/lugar, preferencia place.name */
    const lugar = evento => evento.place?.name ?? evento.recinto ?? null;

    /** Nombre de la división/competición */
    const division = evento => evento.division?.nombre ?? null;

    /** Invitaciones restantes del evento para este abonado */
    const invitacionesRestantes = evento => evento.invitaciones_restantes ?? 0;

    /**
     * Fecha límite de solicitud formateada.
     * Devuelve null si el campo no existe en el payload (fallback seguro).
     */
    const fechaLimiteSolicitud = evento => {
        const raw = evento.fecha_limite_solicitud ?? null;
        if (!raw) return null;
        return parseFechaSesion(raw).dia
            ? `${parseFechaSesion(raw).dia} ${parseFechaSesion(raw).mes}`
            : raw;
    };

    /** Determina si el evento permite generar invitaciones */
    const canGenerar = evento => {
        return invitacionesRestantes(evento) > 0 && evento.all_invitaciones_consumidas !== true;
    };
</script>
